import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, isUnauthorizedApiError, SUPER_TOKEN_KEY } from '../../api/client';

type WizardAgentRow = {
  id: string;
  name: string;
  model: string;
  systemPrompt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  counts: { conversations: number; contextDocuments: number; contextUrls: number };
  wizardSession: {
    ipAddress: string;
    totalTokensUsed: number;
    tokenBudget: number;
    demoToken: string | null;
    status: string;
    createdAt: string;
  } | null;
};

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};

const badge = (color: string, bg: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  color,
  background: bg,
});

export function SuperWizardAgentsPage() {
  const nav = useNavigate();
  const token = localStorage.getItem(SUPER_TOKEN_KEY);
  const [agents, setAgents] = useState<WizardAgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setErr('');
    try {
      const data = await apiGet<WizardAgentRow[]>('/api/super/wizard-agents', token);
      setAgents(data);
    } catch (e) {
      if (isUnauthorizedApiError(e)) {
        localStorage.removeItem(SUPER_TOKEN_KEY);
        nav('/super/login', { replace: true });
        return;
      }
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token, nav]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!token) {
    nav('/super/login', { replace: true });
    return null;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <h1 style={{ fontSize: 22, color: '#0f172a', marginTop: 0, marginBottom: 4 }}>Agentes Landing</h1>
      <p style={{ fontSize: 14, color: '#64748b', marginTop: 0, marginBottom: 20 }}>
        Agentes creados desde el wizard de la landing page. Pertenecen a la organización <code>wizard-sandbox</code> hasta que el usuario complete el registro.
      </p>

      {err ? <p style={{ color: '#b91c1c', fontSize: 14 }}>{err}</p> : null}
      {loading ? (
        <p style={{ color: '#64748b', fontSize: 14 }}>Cargando…</p>
      ) : agents.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>No hay agentes creados desde la landing todavía.</p>
      ) : (
        <>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            {agents.length} agente{agents.length !== 1 ? 's' : ''} en total
          </p>
          {agents.map((a) => (
            <div key={a.id} style={card}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>{a.name}</h3>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                    Modelo: <code style={{ fontSize: 12 }}>{a.model}</code> ·{' '}
                    Creado: {new Date(a.createdAt).toLocaleString()}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  <span style={a.isActive ? badge('#166534', '#dcfce7') : badge('#64748b', '#f1f5f9')}>
                    {a.isActive ? 'Activo' : 'Pausado'}
                  </span>
                  {a.wizardSession ? (
                    <span style={badge('#1e40af', '#dbeafe')}>
                      {a.wizardSession.status}
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Stats row */}
              <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 13, color: '#64748b' }}>
                <span>💬 {a.counts.conversations} conv.</span>
                <span>📄 {a.counts.contextDocuments} docs</span>
                <span>🔗 {a.counts.contextUrls} URLs</span>
                {a.wizardSession ? (
                  <span>🎟️ Tokens: {a.wizardSession.totalTokensUsed.toLocaleString()} / {a.wizardSession.tokenBudget.toLocaleString()}</span>
                ) : null}
              </div>

              {/* Session info */}
              {a.wizardSession ? (
                <div style={{ marginTop: 10, fontSize: 12, color: '#94a3b8' }}>
                  IP: {a.wizardSession.ipAddress} · Sesión: {new Date(a.wizardSession.createdAt).toLocaleString()}
                  {a.wizardSession.demoToken ? (
                    <span> · Demo: <code style={{ fontSize: 11 }}>{a.wizardSession.demoToken.slice(0, 12)}…</code></span>
                  ) : null}
                </div>
              ) : (
                <div style={{ marginTop: 10, fontSize: 12, color: '#94a3b8' }}>
                  Sin sesión de wizard asociada
                </div>
              )}

              {/* Expandable system prompt */}
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => setExpandedId(expandedId === a.id ? null : a.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563eb',
                    fontSize: 13,
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  {expandedId === a.id ? '▾ Ocultar prompt' : '▸ Ver system prompt'}
                </button>
                {expandedId === a.id ? (
                  <pre
                    style={{
                      marginTop: 8,
                      padding: 12,
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#334155',
                      maxHeight: 300,
                      overflow: 'auto',
                    }}
                  >
                    {a.systemPrompt || '(sin system prompt)'}
                  </pre>
                ) : null}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
